import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AnalyticsMetrics {
  readonly revenue_ttc_fcfa: number;
  readonly orders_count: number;
  readonly unique_customers: number;
  readonly overall_conversion_rate: number;
}

export interface AnalyticsDelta {
  readonly [field: string]: {
    readonly pct: number;
    readonly direction: "up" | "down" | "flat";
  };
}

export interface AnalyticsOverviewResponse {
  readonly current: { readonly metrics: AnalyticsMetrics };
  readonly delta: AnalyticsDelta;
}

export interface TrendingProduct {
  readonly product_id: string;
  readonly views: number;
  readonly cart_adds: number;
  readonly purchases: number;
  readonly score: number;
}

export interface TrendingProductsResponse {
  readonly data: { readonly trending: readonly TrendingProduct[] };
}

export interface SearchQueryEntry {
  readonly query: string;
  readonly total_searches: number;
  readonly avg_results_count: number | null;
  readonly click_through_rate: number;
  readonly conversion_rate: number;
}

export interface SearchQueriesResponse {
  readonly data: {
    readonly queries: readonly SearchQueryEntry[];
    readonly zero_result_queries: readonly SearchQueryEntry[];
    readonly total_searches: number;
    readonly zero_result_count: number;
  };
}

export interface ConversionFunnelStage {
  readonly name: string;
  readonly count: number;
  readonly rate_from_previous: number;
  readonly dropoff: number;
}

export interface ConversionFunnelResponse {
  readonly stages: readonly ConversionFunnelStage[];
  readonly overall_rate: number;
  readonly period: string;
  readonly payment_split?: {
    readonly wave: number;
    readonly orange_money: number;
    readonly cod: number;
  };
}

export interface SearchGap {
  readonly query: string;
  readonly search_count: number;
  readonly last_searched_at: string;
}

export interface TrendingSearch {
  readonly query: string;
  readonly total_searches: number;
  readonly click_through_rate: number;
  readonly conversion_rate: number;
}

export interface HighIntentProduct {
  readonly product_id: string;
  readonly product_name: string;
  readonly intent_score: number;
  readonly cart_adds: number;
  readonly purchases: number;
}

export interface WishlistedProduct {
  readonly product_id: string;
  readonly product_name?: string;
  readonly wishlist_count: number;
}

export interface IntentAnalyticsResponse {
  readonly data: {
    readonly search_gaps: readonly SearchGap[];
    readonly trending_searches: readonly TrendingSearch[];
    readonly high_intent_products: readonly HighIntentProduct[];
    readonly top_wishlisted: readonly WishlistedProduct[];
  };
}

export interface SearchFunnelResponse {
  readonly data: {
    readonly period_days: number;
    readonly zero_result_rate: number;
    readonly funnel: {
      readonly searches: number;
      readonly clicks: number;
      readonly cart_adds: number;
      readonly purchases: number;
      readonly search_to_click_rate: number;
      readonly click_to_cart_rate: number;
      readonly cart_to_purchase_rate: number;
    };
  };
}

export type AnalyticsHealthStatus = "ok" | "degraded" | "stale" | "empty";

export interface AnalyticsPipelineHealth {
  readonly name: string;
  readonly status: AnalyticsHealthStatus;
  readonly last_update: string | null;
  readonly lag_hours: number | null;
  readonly row_count: number;
  readonly cron_path: string;
  readonly watermark_status: string | null;
  readonly watermark_error: string | null;
}

export interface AnalyticsHealthResponse {
  readonly status: AnalyticsHealthStatus;
  readonly checked_at: string;
  readonly pipelines: readonly AnalyticsPipelineHealth[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PERIOD_DAYS: Record<"7d" | "30d" | "90d", number> = { "7d": 7, "30d": 30, "90d": 90 };

function buildDateRange(days: number): { from: string; to: string } {
  const now = Date.now();
  const to = new Date(now).toISOString().slice(0, 10);
  const from = new Date(now - days * 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const analyticsApi = {
  overview: (period: "7d" | "30d" | "90d" = "30d") => {
    const { from, to } = buildDateRange(PERIOD_DAYS[period]);
    return adminFetch<AnalyticsOverviewResponse>(
      `/api/admin/analytics/overview?period=daily&from=${from}&to=${to}&compare_to=previous_period`,
    );
  },

  trending: (hours = 720) =>
    adminFetch<TrendingProductsResponse>(
      `/api/admin/analytics/trending?hours=${hours}`,
    ),

  searchQueries: (days = 7) =>
    adminFetch<SearchQueriesResponse>(
      `/api/admin/analytics/search-queries?days=${days}`,
    ),

  conversionFunnel: (days = 7) => {
    const { from, to } = buildDateRange(days);
    return adminFetch<ConversionFunnelResponse>(
      `/api/admin/analytics/funnel?from=${from}&to=${to}`,
    );
  },

  intent: (days = 7) =>
    adminFetch<IntentAnalyticsResponse>(
      `/api/admin/analytics/intent?days=${days}`,
    ),

  searchFunnel: (days = 7) =>
    adminFetch<SearchFunnelResponse>(
      `/api/admin/analytics/search-funnel?days=${days}`,
    ),

  health: () =>
    adminFetch<AnalyticsHealthResponse>(`/api/admin/health/analytics`),
} as const;
